import type { Vec2 } from './vec';
import { add, sub, cross, dist, perp } from './vec';
import { degToRad } from './angle';
import { lineLineIntersect } from './intersect';

const eps = 1e-9;

export interface Triangle {
  a: Vec2;
  b: Vec2;
  c: Vec2;
}

export type CongruenceSpec =
  | { kind: 'SSS'; sides: [number, number, number] }
  | { kind: 'SAS'; sides: [number, number]; angleDeg: number }
  | { kind: 'ASA'; anglesDeg: [number, number]; side: number }
  | { kind: 'AAS'; anglesDeg: [number, number]; side: number }
  | { kind: 'SSA'; sides: [number, number]; angleDeg: number };

/**
 * Strict triangle inequality: sum of any two sides must be strictly greater than the third.
 * Returns false for degenerate or zero-length sides.
 */
export function isValidTriangle(a: number, b: number, c: number): boolean {
  return a + b > c && b + c > a && a + c > b;
}

/**
 * Realize a triangle from a congruence specification.
 * Placement convention: vertex A at origin (0,0), vertex B on the +x axis.
 * Returns null if the specification cannot form a valid triangle.
 */
export function realizeTriangle(spec: CongruenceSpec): Triangle | null {
  switch (spec.kind) {
    case 'SSS': {
      const [s0, s1, s2] = spec.sides;
      if (!isValidTriangle(s0, s1, s2)) {
        return null;
      }
      // A at origin, B on +x axis at distance s0
      const a: Vec2 = { x: 0, y: 0 };
      const b: Vec2 = { x: s0, y: 0 };
      // C from law of cosines: angle at A = acos((s0² + s1² - s2²) / (2·s0·s1))
      const cosA = (s0 * s0 + s1 * s1 - s2 * s2) / (2 * s0 * s1);
      const angleA = Math.acos(Math.max(-1, Math.min(1, cosA)));
      const c: Vec2 = { x: s1 * Math.cos(angleA), y: s1 * Math.sin(angleA) };
      return { a, b, c };
    }

    case 'SAS': {
      const [s0, s1] = spec.sides;
      const angleDeg = spec.angleDeg;
      // A at origin, B at (s0, 0), C at angle angleDeg from +x with distance s1
      const a: Vec2 = { x: 0, y: 0 };
      const b: Vec2 = { x: s0, y: 0 };
      const angleRad = degToRad(angleDeg);
      const c: Vec2 = { x: s1 * Math.cos(angleRad), y: s1 * Math.sin(angleRad) };
      return { a, b, c };
    }

    case 'ASA': {
      const [alphaDeg, betaDeg] = spec.anglesDeg;
      const side = spec.side; // included side AB
      // A at origin, B at (side, 0)
      const a: Vec2 = { x: 0, y: 0 };
      const b: Vec2 = { x: side, y: 0 };
      // C is the intersection of:
      // - ray from A at angle alphaDeg (CCW from +x)
      // - ray from B at angle 180 - betaDeg (CCW from +x, which is inward)
      const alphaRad = degToRad(alphaDeg);
      // Ray from A: A + t * (cos α, sin α)
      const rayA_point1: Vec2 = a;
      const rayA_point2: Vec2 = { x: Math.cos(alphaRad), y: Math.sin(alphaRad) };
      // Ray from B: B + s * (cos(180 - β), sin(180 - β))
      const rayB_angleRad = degToRad(180 - betaDeg);
      const rayB_point1: Vec2 = b;
      const rayB_point2: Vec2 = {
        x: b.x + Math.cos(rayB_angleRad),
        y: b.y + Math.sin(rayB_angleRad),
      };
      const c = lineLineIntersect(rayA_point1, rayA_point2, rayB_point1, rayB_point2);
      if (!c) {
        return null; // Degenerate/parallel rays (shouldn't happen for valid angles)
      }
      return { a, b, c };
    }

    case 'AAS': {
      const [alphaDeg, betaDeg] = spec.anglesDeg;
      const side = spec.side; // opposite the second angle beta
      const gammaDeg = 180 - alphaDeg - betaDeg;
      // Law of sines: side / sin(beta) = AB / sin(gamma)
      const gammaRad = degToRad(gammaDeg);
      const betaRad = degToRad(betaDeg);
      const ab = (side * Math.sin(gammaRad)) / Math.sin(betaRad);
      // Now realize as ASA with angles [alpha, beta] and included side ab
      const result = realizeTriangle({
        kind: 'ASA',
        anglesDeg: [alphaDeg, betaDeg],
        side: ab,
      });
      return result;
    }

    case 'SSA': {
      const [s0, s1] = spec.sides;
      const angleDeg = spec.angleDeg;
      const solutions = ssaSolutions(s0, s1, angleDeg);
      if (solutions.length > 0) {
        return solutions[0]!;
      }
      return null;
    }

    default: {
      const _exhaustive: never = spec;
      return _exhaustive;
    }
  }
}

/**
 * Solve the ambiguous SSA (Side-Side-Angle) case.
 * Given: side a (opposite angle A), side b (adjacent to angle A), and angle A.
 * Returns 0, 1, or 2 solutions.
 *
 * Placement: A at origin, B at (b, 0). The angle at A (between rays AB and AC) is angleA.
 * C must satisfy: |BC| = a (since a is opposite A).
 *
 * Algorithm:
 * h = b * sin(A)
 * - If a < h - eps: no solution (circle centered at A with radius b·sin(A) doesn't reach C)
 * - If |a - h| <= eps: exactly 1 (right angle case)
 * - If h < a < b: 2 solutions
 * - If a >= b: 1 solution
 */
export function ssaSolutions(a: number, b: number, angleADeg: number): Triangle[] {
  const angleARad = degToRad(angleADeg);
  const h = b * Math.sin(angleARad);

  if (a < h - eps) {
    return []; // No solution: a is too small
  }

  // Place canonically: A at origin, B at (b, 0)
  const vertexA: Vec2 = { x: 0, y: 0 };
  const vertexB: Vec2 = { x: b, y: 0 };

  // Find C such that:
  // 1. |BC| = a  (side opposite to angle A)
  // 2. angle CAB = angleA
  // 3. C is in the upper half-plane (y >= 0) for the canonical form

  // C lies on the ray from A at angle angleA: C = (t * cos(angleA), t * sin(angleA)) for some t > 0.
  // We also need |BC|² = a².
  // |C - B|² = (t·cos(A) - b)² + (t·sin(A))² = a²
  // t²·cos²(A) - 2bt·cos(A) + b² + t²·sin²(A) = a²
  // t² - 2bt·cos(A) + b² = a²
  // t² - 2bt·cos(A) + (b² - a²) = 0

  const cosA = Math.cos(angleARad);
  const sinA = Math.sin(angleARad);

  // Quadratic equation: t² - 2b·cos(A)·t + (b² - a²) = 0
  const quadA = 1;
  const quadB = -2 * b * cosA;
  const quadC = b * b - a * a;

  const discriminant = quadB * quadB - 4 * quadA * quadC;

  if (discriminant < -eps) {
    return []; // No real solution
  }

  const solutions: Triangle[] = [];

  if (Math.abs(discriminant) < eps) {
    // One solution (right angle case)
    const t = -quadB / (2 * quadA);
    if (t > eps) {
      const vertexC: Vec2 = { x: t * cosA, y: t * sinA };
      solutions.push({ a: vertexA, b: vertexB, c: vertexC });
    }
  } else {
    // Two potential solutions
    const sqrtDisc = Math.sqrt(discriminant);
    const t1 = (-quadB - sqrtDisc) / (2 * quadA);
    const t2 = (-quadB + sqrtDisc) / (2 * quadA);

    for (const t of [t1, t2]) {
      if (t > eps) {
        const vertexC: Vec2 = { x: t * cosA, y: t * sinA };
        solutions.push({ a: vertexA, b: vertexB, c: vertexC });
      }
    }
  }

  return solutions;
}

/**
 * Centroid: the average of the three vertices.
 */
export function centroid(t: Triangle): Vec2 {
  return {
    x: (t.a.x + t.b.x + t.c.x) / 3,
    y: (t.a.y + t.b.y + t.c.y) / 3,
  };
}

/**
 * Circumcenter: intersection of two perpendicular bisectors.
 * Returns null if the triangle is degenerate (collinear vertices).
 */
export function circumcenter(t: Triangle): Vec2 | null {
  // Perpendicular bisector of AB
  const midAB: Vec2 = { x: (t.a.x + t.b.x) / 2, y: (t.a.y + t.b.y) / 2 };
  const edgeAB: Vec2 = sub(t.b, t.a);
  const perpAB: Vec2 = perp(edgeAB);
  const bisectorAB_p2: Vec2 = add(midAB, perpAB);

  // Perpendicular bisector of AC
  const midAC: Vec2 = { x: (t.a.x + t.c.x) / 2, y: (t.a.y + t.c.y) / 2 };
  const edgeAC: Vec2 = sub(t.c, t.a);
  const perpAC: Vec2 = perp(edgeAC);
  const bisectorAC_p2: Vec2 = add(midAC, perpAC);

  // Find intersection
  return lineLineIntersect(midAB, bisectorAB_p2, midAC, bisectorAC_p2);
}

/**
 * Incenter: weighted average of vertices, where each vertex is weighted
 * by the length of the opposite side.
 * Formula: I = (a·A + b·B + c·C) / perimeter
 * where a = |BC|, b = |AC|, c = |AB|
 */
export function incenter(t: Triangle): Vec2 {
  const a = dist(t.b, t.c); // opposite to vertex A
  const b = dist(t.a, t.c); // opposite to vertex B
  const c = dist(t.a, t.b); // opposite to vertex C
  const perimeter = a + b + c;

  return {
    x: (a * t.a.x + b * t.b.x + c * t.c.x) / perimeter,
    y: (a * t.a.y + b * t.b.y + c * t.c.y) / perimeter,
  };
}

/**
 * Orthocenter: intersection of two altitudes.
 * Each altitude passes through a vertex and is perpendicular to the opposite side.
 */
export function orthocenter(t: Triangle): Vec2 {
  // Altitude from A perpendicular to BC
  const edgeBC: Vec2 = sub(t.c, t.b);
  const perpBC: Vec2 = perp(edgeBC);
  const altA_p2: Vec2 = add(t.a, perpBC);

  // Altitude from B perpendicular to AC
  const edgeAC: Vec2 = sub(t.c, t.a);
  const perpAC: Vec2 = perp(edgeAC);
  const altB_p2: Vec2 = add(t.b, perpAC);

  // Find intersection
  const intersection = lineLineIntersect(t.a, altA_p2, t.b, altB_p2);
  return intersection ?? { x: 0, y: 0 }; // Fallback (shouldn't happen for non-degenerate)
}

/**
 * Check if centroid, circumcenter, and orthocenter are collinear within eps.
 * Returns false if circumcenter is null (degenerate triangle).
 */
export function eulerLineCollinear(t: Triangle, eps: number): boolean {
  const cg = centroid(t);
  const cc = circumcenter(t);
  const oh = orthocenter(t);

  if (!cc) {
    return false; // Degenerate
  }

  // Check collinearity using cross product
  const vec1 = sub(cc, cg);
  const vec2 = sub(oh, cg);
  const crossProd = cross(vec1, vec2);

  return Math.abs(crossProd) <= eps;
}

/**
 * Heron's formula for triangle area.
 * Area = √(s(s-a)(s-b)(s-c)) where s = (a+b+c)/2
 */
export function heron(a: number, b: number, c: number): number {
  const s = (a + b + c) / 2;
  return Math.sqrt(s * (s - a) * (s - b) * (s - c));
}
